import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Card,
  Button,
  Badge,
  Row,
  Col,
  Form,
  InputGroup,
  Dropdown,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  Search,
  Filter,
  Download,
  Plus,
  EyeIcon,
  CreditCard,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import GenererPdfFacture from "./genererPdfFacture";
import { createRoot } from "react-dom/client";

import {
  getAllInvoices,
  getInvoicesByStatus,
  getOverdueInvoices,
  getInvoiceById,
  recordPayment,
} from "../../api/invoices";
import { formatCurrency, formatDate } from "../../utils/formatters";
import Loading from "../common/Loading";

// ✅ Composant PaymentModal extrait et mémorisé avec support des paiements partiels
const PaymentModal = React.memo(
  ({
    showPaymentModal,
    paymentAmount,
    paymentError,
    selectedInvoice,
    onClose,
    onAmountChange,
    onSubmit,
  }) => {
    // Calculer le montant restant à payer
    const montantRestant = selectedInvoice
      ? selectedInvoice.totalAmount - (selectedInvoice.montantPaiement || 0)
      : 0;

    return (
      <Modal show={showPaymentModal} onHide={onClose} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Enregistrement du paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <div className="mb-3">
              <p>
                <strong>Facture N°:</strong> {selectedInvoice.invoiceNumber}
              </p>
              <p>
                <strong>Client:</strong> {selectedInvoice.client.name}
              </p>
              <p>
                <strong>Montant total:</strong>{" "}
                {formatCurrency(selectedInvoice.totalAmount)}
                
              </p>
              {selectedInvoice.montantPaiement > 0 && (
                <p>
                  <strong>Déjà payé:</strong>{" "}
                  {formatCurrency(selectedInvoice.montantPaiement)}
                </p>
              )}
              <p>
                <strong>Montant restant:</strong>{" "}
                {formatCurrency(montantRestant)}
              </p>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Montant du paiement</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={paymentAmount}
                onChange={onAmountChange}
                isInvalid={!!paymentError}
                placeholder="Entrez le montant"
                autoFocus
                inputMode="decimal"
              />
              <InputGroup.Text>FCFA</InputGroup.Text>
            </InputGroup>
            {paymentError && (
              <Form.Text className="text-danger">{paymentError}</Form.Text>
            )}
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              variant="outline-info"
              size="sm"
              onClick={() =>
                onAmountChange({ target: { value: montantRestant.toString() } })
              }
            >
              Paiement complet
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() =>
                onAmountChange({
                  target: { value: (montantRestant / 2).toString() },
                })
              }
            >
              50%
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={!!paymentError || !paymentAmount}
          >
            Confirmer le paiement
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
);

const InvoiceList = ({
  onViewInvoice,
  onCreateInvoice,
  onPreviewInvoice,
  companyInfo,
}) => {
  const { isAdmin, isManager, isSalesRep, isDistributor, user } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // États pour la boîte de dialogue de paiement
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentError, setPaymentError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
  }, [filter]);

  // ✅ Handlers mémorisés pour éviter les re-créations
  const handleModalClose = useCallback(() => {
    setShowPaymentModal(false);
    setPaymentAmount("");
    setPaymentError("");
    setCurrentInvoiceId(null);
    setSelectedInvoice(null);
  }, []);

  const handleAmountChange = useCallback(
    (e) => {
      const value = e.target.value;

      // Permettre seulement les nombres et les décimales
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setPaymentAmount(value);

        if (value && selectedInvoice) {
          const montantSaisi = parseFloat(value);
          const montantRestant =
            selectedInvoice.totalAmount -
            (selectedInvoice.montantPaiement || 0);

          // Validation: montant doit être un nombre positif et ne pas dépasser le montant restant
          if (isNaN(montantSaisi) || montantSaisi <= 0) {
            setPaymentError("Veuillez entrer un montant valide supérieur à 0");
          } else if (montantSaisi > montantRestant) {
            setPaymentError(
              `Le montant ne peut pas dépasser ${formatCurrency(
                montantRestant
              )}`
            );
          } else {
            setPaymentError("");
          }
        } else if (value === "") {
          setPaymentError("");
        }
      }
    },
    [selectedInvoice]
  );

  const handlePaymentSubmit = useCallback(async () => {
    if (
      !paymentAmount ||
      isNaN(paymentAmount) ||
      parseFloat(paymentAmount) <= 0
    ) {
      setPaymentError("Veuillez entrer un montant valide supérieur à 0");
      return;
    }

    const montantSaisi = parseFloat(paymentAmount);
    const montantRestant =
      selectedInvoice.totalAmount - (selectedInvoice.montantPaiement || 0);

    if (montantSaisi > montantRestant) {
      setPaymentError(
        `Le montant ne peut pas dépasser ${formatCurrency(montantRestant)}`
      );
      return;
    }

    try {
      const updatedInvoice = await recordPayment(
        currentInvoiceId,
        montantSaisi
      );

      // Mettre à jour la facture dans la liste locale avec les nouvelles données
      setInvoices((prevInvoices) =>
        prevInvoices.map((invoice) =>
          invoice.id === currentInvoiceId
            ? {
                ...invoice,
                status: updatedInvoice.status,
                montantPaiement: updatedInvoice.montantPaiement,
                paidDate: updatedInvoice.paidDate,
              }
            : invoice
        )
      );

      const messageSucces =
        updatedInvoice.status === "PAID"
          ? "Facture payée intégralement !"
          : `Paiement partiel enregistré. Montant restant: ${formatCurrency(
              updatedInvoice.totalAmount - updatedInvoice.montantPaiement
            )}`;

      setSuccess(messageSucces);
      handleModalClose();

      // Effacer le message de succès après 5 secondes
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(err.message || "Erreur lors de l'enregistrement du paiement");
      handleModalClose();

      // Effacer le message d'erreur après 3 secondes
      setTimeout(() => setError(""), 3000);
    }
  }, [paymentAmount, currentInvoiceId, selectedInvoice, handleModalClose]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError("");

    try {
      let data;

      switch (filter) {
        case "pending":
          data = await getInvoicesByStatus("PENDING");
          break;
        case "paid":
          data = await getInvoicesByStatus("PAID");
          break;
        case "part_pay":
          data = await getInvoicesByStatus("PART_PAY");
          break;
        case "overdue":
          data = await getOverdueInvoices();
          break;
        case "cancelled":
          data = await getInvoicesByStatus("CANCELLED");
          break;
        default:
          data = await getAllInvoices();
      }

      // 🧠 Trie par id décroissante
      const sortedData = [...data].sort((a, b) => {
    
        return b.id - a.id;
      });
      setInvoices(sortedData);
    } catch (err) {
      setError("Impossible de charger les factures. Veuillez réessayer.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  console.log("invoices:", invoices);

  // ✅ Méthode mémorisée pour ouvrir la boîte de dialogue de paiement
  const openPaymentDialog = useCallback(
    (invoiceId) => {
      // Trouver la facture pour obtenir toutes ses informations
      const invoice = invoices.find((inv) => inv.id === invoiceId);
      if (invoice) {
        setCurrentInvoiceId(invoiceId);
        setSelectedInvoice(invoice);
        // Initialiser avec le montant restant à payer
        const montantRestant =
          invoice.totalAmount - (invoice.montantPaiement || 0);
        setPaymentAmount(montantRestant.toString());
        setPaymentError(""); // Reset error
        setShowPaymentModal(true);
      }
    },
    [invoices]
  );

  // Fonction pour télécharger le PDF
  const handleDownloadInvoice = async (invoiceId) => {
    try {
      setLoading(true);
      const invoiceData = await getInvoiceById(invoiceId);

      console.log("Invoice data récupérée :", invoiceData);

      const pdfElement = document.createElement("div");
      pdfElement.style.position = "absolute";
      pdfElement.style.left = "-9999px";
      document.body.appendChild(pdfElement);

      const root = createRoot(pdfElement);
      root.render(
        <GenererPdfFacture
          invoice={invoiceData}
          company={companyInfo}
          onDownload={(blob) => {
            try {
              if (!(blob instanceof Blob)) {
                console.error("Error: Not a valid Blob object", blob);
                setLoading(false);
                return;
              }

              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `facture_${invoiceData.invoiceNumber}.pdf`;
              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              document.body.removeChild(pdfElement);
              root.unmount();
            } catch (error) {
              console.error("Error downloading invoice:", error);
            } finally {
              setLoading(false);
            }
          }}
        />
      );
    } catch (err) {
      console.error("Erreur lors du téléchargement de la facture:", err);
      setError("Impossible de télécharger la facture");
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return <Badge bg="warning">En attente de paiement</Badge>;
      case "PAID":
        return <Badge bg="success">Payée</Badge>;
      case "PART_PAY":
        return <Badge bg="info">Partiellement payée</Badge>;
      case "OVERDUE":
        return <Badge bg="danger">En retard</Badge>;
      case "CANCELLED":
        return <Badge bg="secondary">Annulée</Badge>;
      default:
        return <Badge bg="info">{status}</Badge>;
    }
  };

  // ✅ Filtrage mémorisé
  const filteredInvoices = useMemo(
    () =>
      invoices.filter(
        (invoice) =>
          invoice.invoiceNumber
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          invoice.client.name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (invoice.order.orderNumber &&
            invoice.order.orderNumber
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      ),
    [invoices, searchTerm]
  );

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">Gestion des Factures</h5>
          </Col>
          <Col xs="auto">
            <Button
              variant="primary"
              size="sm"
              onClick={onCreateInvoice}
              className="me-2"
            >
              <Plus size={16} className="me-1" />
              Nouvelle Facture
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="mb-3 g-2">
          <Col md={6} lg={4}>
            <InputGroup>
              <InputGroup.Text>
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Rechercher une facture..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs="auto">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="filter-dropdown">
                <Filter size={18} className="me-1" />
                {filter === "all" && "Toutes les factures"}
                {filter === "pending" && "En attente de paiement"}
                {filter === "paid" && "Payées"}
                {filter === "part_pay" && "Partiellement payées"}
                {filter === "overdue" && "En retard"}
                {filter === "cancelled" && "Annulées"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setFilter("all")}>
                  Toutes les factures
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter("pending")}>
                  En attente de paiement
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter("paid")}>
                  Payées
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter("part_pay")}>
                  Partiellement payées
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter("overdue")}>
                  En retard
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilter("cancelled")}>
                  Annulées
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => fetchInvoices()}>
              Actualiser
            </Button>
          </Col>
        </Row>

        {loading ? (
          <Loading />
        ) : (
          <>
            {filteredInvoices.length === 0 ? (
              <Alert variant="info">
                Aucune facture trouvée pour les critères sélectionnés.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>N° Facture</th>
                      <th>Client</th>
                      <th>N° Commande</th>
                      <th>Date d'émission</th>
                      <th>Date d'échéance</th>
                      <th>Montant total</th>
                      <th>Montant payé</th>
                      <th>Montant restant</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => {
                      const montantPaye = invoice.montantPaiement || 0;
                      const montantRestant =
                        invoice.totalAmount - invoice.montantPaiement;

                      return (
                        <tr key={invoice.id}>
                          <td>{invoice.invoiceNumber}</td>
                          <td>{invoice.client.name}</td>
                          <td>{invoice.order.orderNumber}</td>
                          <td>{formatDate(invoice.issueDate)}</td>
                          <td>{formatDate(invoice.dueDate)}</td>
                          <td>
                            {formatCurrency(invoice.totalAmount)}
                            <br />
                            {invoice.discountAmount > 0 && (
                            <small className="text-muted">
                                Remise : {formatCurrency(invoice.discountAmount)}
                            </small>
                            )}
                          </td>
                          <td>
                            {invoice.montantPaiement > 0 ? (
                              <span className="text-success fw-bold">
                                 {formatCurrency(invoice.montantPaiement)}
                              </span>
                            ) : (
                              <span className="text-muted">0 FCFA</span>
                            )}
                          </td>
                          <td>
                            {montantRestant > 0 ? (
                              <span className="text-warning fw-bold">
                                {formatCurrency(montantRestant)}
                              </span>
                            ) : (
                              <span className="text-muted">0 FCFA</span>
                            )}
                          </td>
                          <td>{getStatusBadge(invoice.status)}</td>
                          <td>
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="me-1"
                              onClick={() => onViewInvoice(invoice.id)}
                              title="Voir les détails"
                            >
                              <EyeIcon size={16} />
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() => handleDownloadInvoice(invoice.id)}
                              title="Télécharger la facture"
                            >
                              <Download size={16} />
                            </Button>
                            {(invoice.status === "PENDING" ||
                              invoice.status === "PART_PAY" ||
                              invoice.status === "OVERDUE") && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => openPaymentDialog(invoice.id)}
                                title={
                                  invoice.status === "PART_PAY"
                                    ? "Compléter le paiement"
                                    : "Enregistrer le paiement"
                                }
                              >
                                <CreditCard size={16} />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* ✅ Modal de paiement avec support des paiements partiels */}
        <PaymentModal
          showPaymentModal={showPaymentModal}
          paymentAmount={paymentAmount}
          paymentError={paymentError}
          selectedInvoice={selectedInvoice}
          onClose={handleModalClose}
          onAmountChange={handleAmountChange}
          onSubmit={handlePaymentSubmit}
        />
      </Card.Body>
    </Card>
  );
};

export default InvoiceList;
